import { useState } from 'react';
import { useApi } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';

export default function SlackSettings() {
    const api = useApi();
    const { tenant, refreshTenant } = useTenant();
    const [webhookUrl, setWebhookUrl] = useState(tenant?.slackWebhookUrl || '');
    const [loading, setLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const isConfigured = !!tenant?.slackWebhookUrl;

    async function handleSave(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            await api.updateSlackSettings({ slackWebhookUrl: webhookUrl || undefined });
            await refreshTenant();
            setMessage({ type: 'success', text: 'Slack settings saved successfully!' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'Failed to save settings' });
        } finally {
            setLoading(false);
        }
    }

    async function handleTestConnection() {
        if (!webhookUrl) {
            setMessage({ type: 'error', text: 'Please enter a webhook URL first' });
            return;
        }

        setTestLoading(true);
        setMessage(null);

        try {
            await api.updateSlackSettings({ slackWebhookUrl: webhookUrl, testConnection: true });
            await refreshTenant();
            setMessage({ type: 'success', text: '✅ Test message sent! Check your Slack channel.' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'Failed to send test message' });
        } finally {
            setTestLoading(false);
        }
    }

    async function handleDisconnect() {
        if (!confirm('Are you sure you want to disconnect Slack?')) return;

        setLoading(true);
        setMessage(null);

        try {
            await api.updateSlackSettings({ slackWebhookUrl: '' });
            setWebhookUrl('');
            await refreshTenant();
            setMessage({ type: 'success', text: 'Slack disconnected successfully' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'Failed to disconnect' });
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="max-w-2xl">
            <h1 className="text-3xl font-bold mb-2 text-text-primary">Slack Integration</h1>
            <p className="text-text-secondary mb-6">
                Connect Slack to receive game notifications in your team's channel.
            </p>

            {/* Status Card */}
            <div className={`mb-6 p-4 rounded-lg border ${isConfigured ? 'bg-green-500/10 border-green-500/30' : 'bg-surface border-border'}`}>
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-gray-500'}`} />
                    <span className={`font-medium ${isConfigured ? 'text-green-400' : 'text-text-secondary'}`}>
                        {isConfigured ? 'Connected to Slack' : 'Not connected'}
                    </span>
                </div>
            </div>

            {message && (
                <div className={`mb-4 p-3 rounded-lg ${message.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
                    {message.text}
                </div>
            )}

            <form onSubmit={handleSave} className="bg-surface rounded-lg shadow p-6 border border-border">
                <div className="mb-6">
                    <label htmlFor="webhookUrl" className="block text-sm font-medium text-text-primary mb-2">
                        Slack Webhook URL
                    </label>
                    <input
                        type="url"
                        id="webhookUrl"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://hooks.slack.com/services/..."
                        className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-text-primary placeholder-text-muted"
                    />
                    <p className="text-xs text-text-muted mt-2">
                        <a
                            href="https://api.slack.com/messaging/webhooks"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            Learn how to create a Slack webhook →
                        </a>
                    </p>
                </div>

                {/* Notifications Preview */}
                <div className="mb-6 p-4 bg-background rounded-lg border border-border">
                    <h3 className="text-sm font-medium text-text-primary mb-3">Notifications you'll receive:</h3>
                    <ul className="space-y-2 text-sm text-text-secondary">
                        <li className="flex items-center gap-2">
                            <span>🎮</span> New game created
                        </li>
                        <li className="flex items-center gap-2">
                            <span>🎉</span> Reveal period started
                        </li>
                        <li className="flex items-center gap-2">
                            <span>🔥</span> Someone completes a bingo line (anonymous)
                        </li>
                        <li className="flex items-center gap-2">
                            <span>🏆</span> Leaderboard updates (top 3)
                        </li>
                    </ul>
                </div>

                <div className="flex flex-wrap gap-3">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : 'Save Settings'}
                    </button>
                    <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={testLoading || !webhookUrl}
                        className="px-4 py-2 border border-primary text-primary rounded-md hover:bg-primary/10 disabled:opacity-50"
                    >
                        {testLoading ? 'Testing...' : '🧪 Test Connection'}
                    </button>
                    {isConfigured && (
                        <button
                            type="button"
                            onClick={handleDisconnect}
                            disabled={loading}
                            className="px-4 py-2 border border-red-500/50 text-red-400 rounded-md hover:bg-red-500/10 disabled:opacity-50"
                        >
                            Disconnect
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}

